import React, { Component } from "react";
import { Tabs, Tab } from "@mui/material";
import { connect } from "react-redux";
import { BackOfficeService, RoleService, ApiService } from "../../api";
import { IconComponent, createNotification, CustomTooltip } from "../../common/components";
import { Flags, isFeatureEnabled, Permissions, translate } from "../../common/providers";
import tabs from "./tabs/tabs";

import { exportView } from "../../navigation";
import styles from "./DSI.module.css";
import { alphaSort, searchInTree, updateTreeBranch } from "../../common/utils";
import { setIsDsi } from "../search-template/company-search-template/slice/company-search-template-slice";

function StyledTabs({ children, onChange, value }) {
	return (
		<Tabs
			classes={{
				root: styles.StyledTabs__root,
				indicator: styles.StyledTabs__indicator,
			}}
			indicatorColor="secondary"
			scrollButtons="auto"
			textColor="secondary"
			value={value}
			variant="scrollable"
			onChange={onChange}
		>
			{children}
		</Tabs>
	);
}

function TabPanel(props) {
	const { children, value, index } = props;
	return (
		<div hidden={value !== index} style={{ display: "flex", height: "100%", overflow: "hidden" }}>
			{value === index && children}
		</div>
	);
}

function formatEnumData(d) {
	const formatted = d?.toLowerCase().split("_").join(" ");
	return {
		name: d,
		displayName: formatted.substring(0, 1).toUpperCase() + formatted.substring(1, formatted.length),
	};
}

const mapDispatchToProps = (dispatch) => ({
	onSetIsDsi: (boolean) => dispatch(setIsDsi(boolean)),
});
const findTabIndexByParam = (param) => {
	const enabledTabs = tabs.filter((t) => !t?.disabled && (!t?.flag || isFeatureEnabled(t.flag)));
	return enabledTabs.findIndex((i) => i.tab.param === param);
};
const indexToParam = (index) => {
	const enabledTabs = tabs.filter(({ disabled, flag }) => !disabled && (!flag || isFeatureEnabled(flag)));
	if (index < 0 || index >= enabledTabs.length) {
		return null;
	}
	return enabledTabs[index]?.tab?.param ?? null;
};

class DSI extends Component {
	constructor(props) {
		super(props);
		this.state = {
			value: 0,
			companies: [],
			userRolesInCompany: [],
			companyRolesInProject: [],
			activeUsersInCompany: [],
		};
		this.cancelTokenSource = ApiService.getCancelTokenSource();
	}

	componentDidMount() {
		const { onSetIsDsi, params } = this.props;
		const { tab, subTab } = params;
		const documentTitle = translate("dsi.document.title");
		document.title = documentTitle;
		this.getUserRolesInCompany();
		this.getUserRolesInProject();
		this.getCompanyRolesInProject();
		this.getAllCompanies();
		onSetIsDsi(true);

		if (tab) {
			const index = findTabIndexByParam(tab);
			if (index >= 0) {
				this.setState({ value: index });
				let canonical = `/dsi/${tab}`;
				if (subTab) {
					canonical += `/${subTab}`;
				}
				window.history.replaceState({}, "", canonical + window.location.search);
			}
		}
	}

	componentWillUnmount() {
		ApiService.cancelTokens(this.cancelTokenSource);
	}

	componentDidUpdate(prevProps) {
		const { params } = this.props;
		const { tab } = params;
		const prevTab = prevProps.params?.tab;

		// Handle tab changes when navigating within DSI
		if (tab && tab !== prevTab) {
			const index = findTabIndexByParam(tab);
			if (index >= 0) {
				this.setState({ value: index });
			}
		}
	}

	handleChange = (event, newValue) => {
		const { navigate } = this.props;
		this.setState({ value: newValue });

		const param = indexToParam(newValue);
		if (!param) {
			return;
		}
		navigate(`/dsi/${param}`, { replace: true });
	};

	getUserRolesInCompany() {
		RoleService.listUserInCompany(this.cancelTokenSource.token)
			.then((data) => {
				if (Array.isArray(data)) {
					const formattedData = data.map((d) => formatEnumData(d));
					this.setState({ userRolesInCompany: formattedData });
				}
			})
			.catch((err) => console.error(err));
	}

	getUserRolesInProject() {
		RoleService.listUserInProject(this.cancelTokenSource.token)
			.then((data) => {
				if (Array.isArray(data)) {
					const formattedData = data.map((d) => formatEnumData(d));
					this.setState({ userRolesInProject: formattedData });
				}
			})
			.catch((err) => console.error(err));
	}

	getCompanyRolesInProject() {
		RoleService.listCompanyInProject(this.cancelTokenSource.token)
			.then((data) => {
				if (Array.isArray(data)) {
					const formattedData = data.map((d) => formatEnumData(d));
					this.setState({ companyRolesInProject: formattedData });
				}
			})
			.catch((err) => console.error(err));
	}

	getAllCompanies() {
		BackOfficeService.getAllCompanies(this.cancelTokenSource.token)
			.then((data) => {
				data.sort((d1, d2) => alphaSort(d1.companyName, d2.companyName));
				this.setState({ companies: data });
			})
			.catch((err) => console.error(err));
	}

	createCompany(payload) {
		BackOfficeService.createCompany(payload, this.cancelTokenSource.token)
			.then((data) => {
				const { companies } = this.state;
				this.setState({ companies: [...companies, data] });
			})
			.catch((err) => {
				console.error(err);
			});
	}

	isValidCompany(company) {
		return company.type === "COMPANY" || company.type === "BUSINESS_UNIT" || company.type === "CONSORTIUM";
	}

	createBu(payload) {
		const { companyId, ...rest } = payload;
		BackOfficeService.createBu({ companyId }, { payload: rest }, this.cancelTokenSource.token)
			.then((data) => {
				const { companies } = this.state;
				const company = searchInTree({
					tree: companies,
					idToFind: companyId,
					additionalConditions: this.isValidCompany,
				});
				const updatedTree = updateTreeBranch({
					tree: companies,
					idToReplace: data.parentId,
					newBranch: {
						...company,
						children: [...(company.children || []), data],
					},
					iterativeBranchName: "children",
				});
				this.setState({ companies: updatedTree });
			})
			.catch((err) => {
				console.error(err);
			});
	}

	updateCompany(companyId, payload) {
		BackOfficeService.updateCompany({ companyId }, payload, this.cancelTokenSource.token)
			.then((data) => {
				const { companies } = this.state;
				const company = searchInTree({
					tree: companies,
					idToFind: companyId,
					additionalConditions: this.isValidCompany,
				});
				const updatedTree = updateTreeBranch({
					tree: companies,
					idToReplace: companyId,
					newBranch: { ...company, ...data },
					iterativeBranchName: "children",
				});
				this.setState({ companies: updatedTree });
			})
			.catch((err) => {
				console.error(err);
			});
	}

	changeBlockForUser({ user, callBack }) {
		if (!user) {
			return;
		}
		BackOfficeService.changeBlockUserOnPlatform({ userId: user.id }, this.cancelTokenSource.token)
			.then(() => {
				callBack();
			})
			.catch((err) => {
				console.error(err);
			});
	}

	getCompanyActiveUsers(company) {
		if (!company) {
			return;
		}
		const companyId = company.id;
		BackOfficeService.getActiveUsersByCompany({ companyId }, this.cancelTokenSource.token)
			.then((data) => {
				this.setState({ activeUsersInCompany: data });
			})
			.catch((err) => {
				console.error(err);
			});
	}

	createUser(company, payload) {
		if (!company) {
			return;
		}
		const companyId = company.id;
		BackOfficeService.createUser({ companyId }, payload, this.cancelTokenSource.token)
			.then((data) => {
				const { companies } = this.state;
				const selectedCompany = searchInTree({
					tree: companies,
					idToFind: companyId,
					additionalConditions: this.isValidCompany,
				});
				if (selectedCompany === null) {
					throw Error();
				}
				const updatedTree = updateTreeBranch({
					tree: companies,
					idToReplace: selectedCompany.id,
					newBranch: {
						...selectedCompany,
						users: [...(selectedCompany.users || []), data],
					},
					iterativeBranchName: "children",
				});
				this.setState({ companies: updatedTree });
			})
			.catch((err) => {
				console.error(err);
			});
	}

	regeneratePwdForUser(user) {
		if (!user) {
			return;
		}
		const userId = user.id;
		BackOfficeService.generateNewPasswordForUser({ userId }, this.cancelTokenSource.token)
			.then((data) =>
				createNotification({
					title: translate("dsi.notification.success.click-me-to-copy"),
					timeout: 20000,
					callback: () => {
						this.copyToClipboard(data?.password?.trim() || "");
					},
				})
			)
			.catch((err) => {
				console.error(err);
			});
	}

	copyToClipboard(str) {
		navigator.clipboard
			.writeText(str)
			.then(() =>
				createNotification({
					type: "success",
					message: translate("dsi.notification.success.copied-to-clipboard"),
				})
			)
			.catch((err) => {
				console.error(err);
			});
	}

	updateCompanyRoleInProject({ company, project, role }) {
		if (!company || !project || !role) {
			return;
		}
		const companyId = company.id;
		const projectId = project.id;
		BackOfficeService.updateCompanyRoleInProject({ companyId, projectId }, role, this.cancelTokenSource.token)
			.then(() => {
				const { companies } = this.state;
				const cny = searchInTree({
					tree: companies,
					idToFind: companyId,
					additionalConditions: this.isValidCompany,
				});
				if (cny === null) {
					throw new Error();
				}
				const updatedTree = updateTreeBranch({
					tree: companies,
					idToReplace: companyId,
					newBranch: {
						...cny,
						role,
					},
					iterativeBranchName: "children",
				});
				this.setState({ companies: updatedTree });
			})
			.catch((err) => {
				console.error(err);
			});
	}

	addExistingPartnerToProject({ selectedCompany, project, company }) {
		if (!company || !project) {
			return;
		}
		const companyId = selectedCompany.id;
		const projectId = project.id;
		BackOfficeService.addExistingPartnerToProject(
			{ companyId: company.id, projectId },
			this.cancelTokenSource.token
		)
			.then(() => {
				const { companies } = this.state;
				if (!companies.some((c) => c.id === companyId)) {
					throw Error();
				}
				const cny = companies.find((c) => c.id === companyId);
				const cnyProject = cny.projects.find((u) => u.id === projectId);
				cnyProject.companies.push(company);
				this.setState({ companies });
			})
			.catch((err) => {
				console.error(err);
			});
	}

	addExistingUserToProject({ company, user, project }) {
		if (!company || !project || !user) {
			return;
		}
		const companyId = company.id;
		const projectId = project.id;
		const userId = user.id;
		BackOfficeService.addExistingUserToProject({ companyId, projectId, userId }, this.cancelTokenSource.token)
			.then((data) => {
				const { companies } = this.state;
				if (!companies.some((c) => c.id === companyId)) {
					throw Error();
				}
				const cny = companies.find((c) => c.id === companyId);
				const cnyProject = cny.projects.find((u) => u.id === projectId);
				cnyProject.users.push(data);
				this.setState({ companies });
			})
			.catch((err) => {
				console.error(err);
			});
	}

	updateUserStatusInProject({ company, selectedUser, project, selectedStatus }) {
		const companyId = company.id;
		const projectId = project.id;
		const userId = selectedUser.id;
		const status = JSON.stringify(selectedStatus);
		BackOfficeService.updateUserStatusInProject(
			{ companyId, userId, projectId, status },
			this.cancelTokenSource.token
		)
			.then(() => {
				const { users } = project;
				const isProject = Array.isArray(users);
				if (!isProject) {
					const cnyUser = company.users.find((u) => u.id === userId);
					const cnyProject = cnyUser.projects.find((p) => p.id === projectId);
					cnyProject.statusInProject = selectedStatus;
					const { companies: c } = this.state;
					this.setState({ companies: c });
				}
				if (isProject) {
					const user = users.find((us) => us.id === userId);
					user.statusInProject = selectedStatus;
					const { users: u } = this.state;
					this.setState({ users: u });
				}
			})
			.catch((err) => {
				console.error(err);
			});
	}

	render() {
		const {
			value,
			companies,
			userRolesInCompany,
			userRolesInProject,
			companyRolesInProject,
			activeUsersInCompany,
		} = this.state;
		return (
			<div className={styles.container}>
				<StyledTabs value={value} onChange={this.handleChange}>
					{tabs
						.filter((tab) => !tab?.disabled && (!tab?.flag || isFeatureEnabled(tab.flag)))
						.map((i) => (
							<Tab
								key={i.tab.title}
								label={
									<CustomTooltip title={translate(i.tab.tooltip)}>
										<span>
											<IconComponent color="var(--grey-600)" icon={i.tab.icon} size="sm" />
											<span className={styles.title}>{translate(i.tab.title)}</span>
										</span>
									</CustomTooltip>
								}
							/>
						))}
				</StyledTabs>
				{tabs
					.filter((tab) => !tab?.disabled && (!tab?.flag || isFeatureEnabled(tab.flag)))
					.map((i, index) => (
						<TabPanel key={i.tab.title} index={index} value={value}>
							{i.panel({
								companies,
								userRolesInCompany,
								userRolesInProject,
								companyRolesInProject,
								activeUsersInCompany,
								createCompany: this.createCompany.bind(this),
								createBu: this.createBu.bind(this),
								updateCompany: this.updateCompany.bind(this),
								changeBlockUser: this.changeBlockForUser.bind(this),
								getCompanyActiveUsers: this.getCompanyActiveUsers.bind(this),
								createUser: this.createUser.bind(this),
								regeneratePwdForUser: this.regeneratePwdForUser.bind(this),
								updateCompanyRoleInProject: this.updateCompanyRoleInProject.bind(this),
								addExistingPartnerToProject: this.addExistingPartnerToProject.bind(this),
								addExistingUserToProject: this.addExistingUserToProject.bind(this),
								updateUserStatusInProject: this.updateUserStatusInProject.bind(this),
							})}
						</TabPanel>
					))}
			</div>
		);
	}
}

export { default as dsiSlice } from "./slice/dsi-slice";
export default exportView({
	path: [{ path: "/dsi/" }, { path: ":tab/:subTab?" }],
	localesPath: "/dsi/locales",
	component: connect(null, mapDispatchToProps)(DSI),
	flag: Flags.BACK_OFFICE,
	role: Permissions.PLATFORM_ADMIN,
});
